"use client";

import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useEffect, useState, useRef } from "react";

interface TimelineStep {
  id: number;
  title: string;
  description: string;
  imageSrc: string;
  imageAlt: string;
}

export function Timeline() {
  const t = useTranslations("pricing");

  const timelineSteps: TimelineStep[] = [
    {
      id: 1,
      title: t("timeline_title_1"),
      description: t("timeline_desc_1"),
      imageSrc: "/client/timeline-1.webp",
      imageAlt: "Woman showing fitness app on smartphone",
    },
    {
      id: 2,
      title: t("timeline_title_2"),
      description: t("timeline_desc_2"),
      imageSrc: "/client/timeline-2.webp",
      imageAlt: "Fitness training app interface on phone",
    },
    {
      id: 3,
      title: t("timeline_title_3"),
      description: t("timeline_desc_3"),
      imageSrc: "/client/timeline-3.webp",
      imageAlt: "Woman using workout app on phone at home gym",
    },
    {
      id: 4,
      title: t("timeline_title_4"),
      description: t("timeline_desc_4"),
      imageSrc: "/client/timeline-4.webp",
      imageAlt: "Personal training session with coach and client",
    },
  ];

  const [activeSteps, setActiveSteps] = useState<number[]>([]);
  const [lineProgress, setLineProgress] = useState(0);
  const timelineRef = useRef<HTMLDivElement>(null);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const newActiveSteps: number[] = [];
        let maxProgress = 0;

        entries.forEach((entry) => {
          const stepIndex = Number(entry.target.getAttribute("data-step")) - 1;

          if (entry.isIntersecting) {
            newActiveSteps.push(stepIndex + 1);

            // Calculate line progress based on intersection ratio and step position
            const progress =
              ((stepIndex + entry.intersectionRatio) / timelineSteps.length) *
              100;
            maxProgress = Math.max(maxProgress, progress);
          }
        });

        // Always include steps that have been passed
        const sortedSteps = Array.from(
          new Set([...activeSteps, ...newActiveSteps])
        ).sort((a, b) => a - b);
        setActiveSteps(sortedSteps);

        // Update line progress
        if (newActiveSteps.length > 0) {
          const highestActiveStep = Math.max(...newActiveSteps);
          const baseProgress =
            ((highestActiveStep - 1) / (timelineSteps.length - 1)) * 100;
          setLineProgress(Math.min(100, Math.max(baseProgress, maxProgress)));
        }
      },
      {
        threshold: [0, 0.25, 0.5, 0.75, 1],
        rootMargin: "-20% 0px -20% 0px",
      }
    );

    stepRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [activeSteps, timelineSteps.length]);

  return (
    <div ref={timelineRef} className="relative max-w-6xl mx-auto">
      {/* Central vertical line container */}
      <div className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-gray-200 h-full hidden md:block">
        {/* Animated fill line */}
        <div
          className="w-full bg-gradient-to-b from-primary to-second-primary transition-all duration-1000 ease-out"
          style={{ height: `${lineProgress}%` }}
        />
      </div>

      {timelineSteps.map((step, index) => (
        <div
          key={step.id}
          ref={(el) => {
            stepRefs.current[index] = el;
          }}
          data-step={step.id}
          className="relative mb-16 md:mb-24"
        >
          {/* Timeline circle with number */}
          <div className="absolute left-1/2 transform -translate-x-1/2 z-10 hidden md:flex">
            <div
              className={cn(
                "w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all duration-1000 ease-out",
                activeSteps.includes(step.id)
                  ? "bg-gradient-to-r from-primary to-second-primary scale-110"
                  : "bg-gray-300 scale-100"
              )}
            >
              <span
                className={cn(
                  "font-bold text-lg transition-colors duration-500",
                  activeSteps.includes(step.id) ? "text-white" : "text-gray-600"
                )}
              >
                {step.id}
              </span>
            </div>
          </div>

          {/* Content container */}
          <div
            className={cn(
              "grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-48 items-center transition-all duration-1000 ease-out",
              index % 2 === 0 ? "md:text-right" : "md:text-left",
              activeSteps.includes(step.id)
                ? "opacity-100 translate-y-0"
                : "opacity-70 translate-y-4"
            )}
          >
            {/* Image */}
            <div
              className={cn(
                "order-1 transition-all duration-1000 ease-out",
                index % 2 === 0 ? "md:order-1" : "md:order-2",
                activeSteps.includes(step.id)
                  ? "transform scale-100"
                  : "transform scale-95"
              )}
            >
              <div className="relative">
                <Image
                  src={step.imageSrc}
                  alt={step.imageAlt}
                  width={400}
                  height={400}
                  className={cn(
                    "w-full h-64 md:h-80 object-cover rounded-2xl shadow-lg transition-all duration-1000 ease-out",
                    activeSteps.includes(step.id) ? "shadow-xl" : "shadow-lg"
                  )}
                />
                {/* Mobile number overlay */}
                <div
                  className={cn(
                    "absolute -top-4 -left-4 w-10 h-10 rounded-full flex items-center justify-center shadow-lg md:hidden transition-all duration-500 ease-out",
                    activeSteps.includes(step.id)
                      ? "bg-gradient-to-r from-primary to-second-primary scale-110"
                      : "bg-gray-300 scale-100"
                  )}
                >
                  <span
                    className={cn(
                      "font-bold transition-colors duration-500",
                      activeSteps.includes(step.id)
                        ? "text-white"
                        : "text-gray-600"
                    )}
                  >
                    {step.id}
                  </span>
                </div>
              </div>
            </div>

            {/* Text content */}
            <div
              className={cn(
                "order-2 space-y-4 transition-all duration-1000 ease-out",
                index % 2 === 0 ? "md:order-2 md:pr-16" : "md:order-1 md:pl-16",
                activeSteps.includes(step.id)
                  ? "translate-x-0"
                  : index % 2 === 0
                  ? "translate-x-4"
                  : "-translate-x-4"
              )}
            >
              <h3
                className={cn(
                  "text-2xl md:text-3xl font-bold tracking-wide transition-colors duration-500",
                  activeSteps.includes(step.id)
                    ? "text-gray-900"
                    : "text-gray-500"
                )}
              >
                {step.title}
              </h3>
              <p
                className={cn(
                  "leading-relaxed text-base md:text-lg transition-colors duration-500",
                  activeSteps.includes(step.id)
                    ? "text-gray-600"
                    : "text-gray-400"
                )}
              >
                {step.description}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
